// src/App.tsx - Updated with About page and code splitting
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext.tsx';
import AuthProvider from './context/AuthContext.tsx';
import { SubscriptionProvider } from './context/SubscriptionContext.tsx';
import { MusicProvider } from './context/MusicContext.tsx';
import { Header, Footer } from './components/Navigation';
import ScrollToTop from './components/ScrollToTop.tsx';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import RouteErrorFallback from './components/RouteErrorBoundary.tsx';
import { PageTracker } from './components/PageTracker.tsx';
// Lazy load ALL route-level components (including Home) to reduce main bundle size
const Home = lazy(() => import('./pages/Home.tsx'));
const About = lazy(() => import('./pages/About.tsx'));
const MichaelConnell = lazy(() => import('./pages/MichaelConnell.tsx'));
const Journal = lazy(() => import('./pages/Journal.tsx'));
const Contact = lazy(() => import('./pages/Contact.tsx'));
const LofiZone = lazy(() => import('./pages/LofiZone.tsx'));
const Community = lazy(() => import('./pages/Community.tsx'));
const Resources = lazy(() => import('./pages/Resources.tsx'));
const FoundersCorner = lazy(() => import('./pages/FoundersCorner.tsx'));
const Toolkit = lazy(() => import('./pages/Toolkit.tsx'));
const Support = lazy(() => import('./pages/Support.tsx'));
const AdminPanel = lazy(() => import('./pages/AdminPanel.tsx'));
const Profile = lazy(() => import('./pages/Profile.tsx'));
const Billing = lazy(() => import('./pages/Billing.tsx'));
const Pricing = lazy(() => import('./pages/Pricing.tsx'));
const Download = lazy(() => import('./pages/Download.tsx'));
const LegalPage = lazy(() => import('./pages/LegalPage.tsx'));
const Wayfinder = lazy(() => import('./pages/Wayfinder.tsx'));
const Blog = lazy(() => import('./pages/Blog.tsx'));
const BlogPost = lazy(() => import('./pages/BlogPost.tsx'));
const NotFound = lazy(() => import('./pages/NotFound.tsx'));
import AuthModal from './components/auth/AuthModal.tsx';
// Lazy-load floating panels — they are not needed for initial render
const GlobalEmberWrapper = lazy(() => import('./components/GlobalEmber/GlobalEmberWrapper'));
const GlobalLofiZoneWrapper = lazy(() => import('./components/GlobalLofiZone/GlobalLofiZoneWrapper'));
import { FloatingPanelProvider } from './context/FloatingPanelContext.tsx';
import UpdateNotification from './components/UpdateNotification.tsx';
import InstallPrompt from './components/InstallPrompt.tsx';
import { CookieConsentBanner } from './components/CookieConsent';

const App: React.FC = () => {
  return (
    <ErrorBoundary
      level="page"
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-orange-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Application Error</h1>
            <p className="text-gray-600 mb-4">
              Life Re:Scripted encountered a critical error. Please refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
            >
              Refresh Page
            </button>
          </div>
        </div>
      }
    >
      <AuthProvider>
        <SubscriptionProvider>
          <ThemeProvider>
            <MusicProvider>
              <FloatingPanelProvider>
              <Router>
              {/* ScrollToTop component - fixes scroll position issues */}
              <ScrollToTop />
              {/* Page tracking for analytics */}
              <PageTracker />

              {/* Update Notification - PWA update banner */}
              <UpdateNotification />
              {/* Install Prompt - PWA install banner */}
              <InstallPrompt />
              {/* Cookie Consent Banner - Analytics consent (GA4, PostHog) */}
              <CookieConsentBanner />
              {/* Auth Modal - Available everywhere */}
              <AuthModal />
              {/* Global LoFi Zone & Ember - lazy-loaded floating panels (not needed for initial paint) */}
              <Suspense fallback={null}>
                <GlobalLofiZoneWrapper />
                <GlobalEmberWrapper />
              </Suspense>

              {/* App layout */}
              <div className="relative z-0">
                <Header />

                <main>
                <ErrorBoundary
                  level="route"
                  fallback={<RouteErrorFallback routeName="application" />}
                >
                  <Suspense
                    fallback={
                      <div className="min-h-screen flex items-center justify-center">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-phoenix-500 mx-auto mb-4"></div>
                          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                        </div>
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/michael-connell" element={<MichaelConnell />} />
                      <Route path="/journal" element={<Journal />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/lofi" element={<LofiZone />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/billing" element={<Billing />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/download" element={<Download />} />
                      <Route path="/community" element={<Community />} />
                      <Route path="/wellness" element={<Navigate to="/forge" replace />} />
                      <Route path="/wayfinder" element={<Wayfinder />} />
                      <Route path="/resources" element={<Resources />} />
                      <Route path="/support" element={<Support />} />
                      <Route path="/founders-corner" element={<FoundersCorner />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/forge" element={<Toolkit />} />
                      <Route path="/toolkit" element={<Toolkit />} /> {/* Legacy redirect */}
                      <Route path="/admin" element={<AdminPanel />} />
                      <Route path="/legal" element={<LegalPage />} />
                      <Route path="/privacy" element={<LegalPage />} />
                      <Route path="/terms" element={<LegalPage />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
                </main>

                <Footer />
              </div>
            </Router>
            </FloatingPanelProvider>
          </MusicProvider>
        </ThemeProvider>
      </SubscriptionProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
